/**
 * @graphql cc
 */
import gql from 'graphql-tag';
import { useEffect, useContext } from 'react';
import {
	// eslint-disable-next-line graphql-relay-compat/no-import-apollo-functions -- Read http://go/cfe-no-import-apollo-functions
	useQuery,
} from 'react-apollo';

import { useIsLicensed } from '@confluence/session-data/entry-points/useAccessStatus.ts';
import { markErrorAsHandled } from '@confluence/graphql/entry-points/markErrorAsHandled.ts';
import { isUnauthorizedError } from '@confluence/error-boundary/entry-points/isUnauthorizedError.ts';
import { SPAViewContext } from '@confluence/spa-view-context/entry-points/SPAViewContext.ts';

import type { useQueryInvitationUrlCreateInvitationUrlQuery as useQueryInvitationUrlCreateInvitationUrlQuery$data } from './__types__/useQueryInvitationUrlCreateInvitationUrlQuery.ts';

type UseQueryInvitationUrlProps = { skipQuery?: boolean };

export const useQueryInvitationUrl = ({ skipQuery = false }: UseQueryInvitationUrlProps) => {
	const isLicensed = useIsLicensed();

	const {
		isSiteAdmin,
		loading: spaViewContextLoading,
		error: spaViewContextError,
	} = useContext(SPAViewContext);

	const isAdmin = isSiteAdmin && !spaViewContextLoading && !spaViewContextError;

	const { data, loading, error } = useQuery<useQueryInvitationUrlCreateInvitationUrlQuery$data>(
		gql`
			query useQueryInvitationUrlCreateInvitationUrlQuery {
				invitationUrls {
					urls {
						id
						url
						status
					}
				}
			}
		`,
		{
			skip: skipQuery || !isLicensed || !isAdmin,
		},
	);

	// check if url already created and active,
	const existingInviteLink = data?.invitationUrls?.urls?.[0]?.url || '';
	const existingInviteLinkStatus = data?.invitationUrls?.urls?.[0]?.status;

	useEffect(() => {
		if (error) {
			if (isUnauthorizedError(error)) {
				markErrorAsHandled(error);
			}
		}
	}, [error]);

	return {
		existingInviteLink,
		existingInviteLinkStatus,
		loading,
		error,
	};
};
